import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  AdminPanelSettings as AdminIcon,
  Person as PersonIcon,
  FitnessCenter as TrainerIcon,
  PointOfSale as ReceptionIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useAuthStore } from '../../store';

const RoleManagement = () => {
  const { user } = useAuthStore();
  
  const [users, setUsers] = useState([]);
  const [availableRoles, setAvailableRoles] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalUsers, setTotalUsers] = useState(0);
  
  // Dialog state
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('');

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (!user) {
        setError('Not authenticated. Please log in.');
        setLoading(false);
        return;
      }

      const idToken = await user.getIdToken();
      const headers = { Authorization: `Bearer ${idToken}` };

      const [usersRes, rolesRes, summaryRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/user-roles/users`, {
          headers,
          params: {
            page: page + 1, // Backend uses 1-indexed pages
            page_size: rowsPerPage,
          },
        }),
        axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/user-roles/roles`, { headers }),
        axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/user-roles/summary`, { headers }),
      ]);

      setUsers(usersRes.data.users);
      setTotalUsers(usersRes.data.total);
      setAvailableRoles(rolesRes.data.roles);
      setSummary(summaryRes.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load role data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenAssignDialog = (user) => {
    setSelectedUser(user);
    setSelectedRole('');
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setSelectedRole('');
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !selectedRole) return;

    try {
      if (!user) {
        setError('Not authenticated');
        return;
      }

      const idToken = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user-roles/users/${selectedUser.id}/roles`,
        { role: selectedRole },
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      setSuccess(`Role "${selectedRole}" assigned to ${selectedUser.full_name}`);
      handleCloseDialog();
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to assign role');
    }
  };

  const handleRemoveRole = async (userId, role, userName) => {
    if (!window.confirm(`Remove role "${role}" from ${userName}?`)) return;

    try {
      if (!user) {
        setError('Not authenticated');
        return;
      }

      const idToken = await user.getIdToken();
      await axios.delete(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/user-roles/users/${userId}/roles/${role}`,
        { headers: { Authorization: `Bearer ${idToken}` } }
      );

      setSuccess(`Role "${role}" removed from ${userName}`);
      fetchData();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to remove role');
    }
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'admin':
        return <AdminIcon fontSize="small" />;
      case 'trainer':
        return <TrainerIcon fontSize="small" />;
      case 'external_trainer':
        return <TrainerIcon fontSize="small" />;
      case 'receptionist':
        return <ReceptionIcon fontSize="small" />;
      default:
        return <PersonIcon fontSize="small" />;
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'trainer':
        return 'primary';
      case 'external_trainer':
        return 'info';
      case 'receptionist':
        return 'secondary';
      default:
        return 'default';
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h5" gutterBottom sx={{ mb: 3, fontWeight: 'bold' }}>
        Zarządzanie Rolami Użytkowników
      </Typography>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {/* Summary Cards */}
      {summary && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  Wszyscy użytkownicy
                </Typography>
                <Typography variant="h4">{summary.total_users}</Typography>
              </CardContent>
            </Card>
          </Grid>
          {summary.role_counts.map((rc) => (
            <Grid item xs={12} sm={6} md={3} key={rc.role}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" gap={1}>
                    {getRoleIcon(rc.role)}
                    <Typography color="textSecondary" gutterBottom>
                      {rc.role}
                    </Typography>
                  </Box>
                  <Typography variant="h4">{rc.count}</Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Imię i nazwisko</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Role</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.id}</TableCell>
                <TableCell>{user.full_name}</TableCell>
                <TableCell>{user.email || '-'}</TableCell>
                <TableCell>
                  <Box display="flex" gap={0.5} flexWrap="wrap">
                    {user.roles.map((role) => (
                      <Chip
                        key={role}
                        label={role}
                        size="small"
                        color={getRoleColor(role)}
                        icon={getRoleIcon(role)}
                        onDelete={() => handleRemoveRole(user.id, role, user.full_name)}
                        deleteIcon={<DeleteIcon />}
                      />
                    ))}
                  </Box>
                </TableCell>
                <TableCell>
                  {user.is_admin && (
                    <Chip label="Admin" size="small" color="error" />
                  )}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Przypisz rolę">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => handleOpenAssignDialog(user)}
                    >
                      <AddIcon />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={totalUsers}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          labelRowsPerPage="Wierszy na stronę:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} z ${count !== -1 ? count : `więcej niż ${to}`}`
          }
        />
      </TableContainer>

      {/* Assign Role Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          Przypisz rolę użytkownikowi: {selectedUser?.full_name}
        </DialogTitle>
        <DialogContent>
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Wybierz rolę</InputLabel>
            <Select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              label="Wybierz rolę"
            >
              {availableRoles.map((role) => (
                <MenuItem
                  key={role.name}
                  value={role.name}
                  disabled={selectedUser?.roles.includes(role.name)}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {getRoleIcon(role.name)}
                    <Box>
                      <Typography variant="body1">{role.display_name}</Typography>
                      <Typography variant="caption" color="textSecondary">
                        {role.description}
                      </Typography>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Anuluj</Button>
          <Button
            onClick={handleAssignRole}
            variant="contained"
            disabled={!selectedRole}
          >
            Przypisz
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RoleManagement;
